// Route strings keep the `:param` format PARENT_ROUTE_FEATURE is keyed on; nothing here may throw into the router.

import type { AnyRouter } from '@tanstack/react-router';
import { normalizeParentPath, pickParams, routeToFeature } from '@tupai/analytics';
import { capturePageview, setFeature, setRoute } from '@tupai/analytics/client';

// The first navigation is seen by both onResolved and the catch-up emit; this drops the repeat.
let lastEmitted: string | null = null;

type Match = {
  fullPath?: string;
  routeId?: string;
  search?: unknown;
  _notFound?: boolean;
};

function routeTemplate(router: AnyRouter, matches: Match[]): string {
  if (matches.some((m) => m._notFound) || matches.at(-1)?.routeId === '/not-found') return '*';
  const leaf = matches.at(-1);
  const fullPath = leaf && leaf.routeId !== '__root__' ? leaf.fullPath : undefined;
  if (!fullPath) return normalizeParentPath(router.state.location.pathname);
  const template = fullPath.replace(/\/\$(\w+)/g, '/:$1');
  return template.length > 1 ? template.replace(/\/+$/, '') : template;
}

function emit(router: AnyRouter): void {
  try {
    const matches = router.state.matches as Match[];
    const routeId = routeTemplate(router, matches);
    const href = typeof window === 'undefined' ? '' : window.location.href;
    const key = `${routeId}|${href}`;
    if (key === lastEmitted) return;
    lastEmitted = key;

    // Set explicitly so an unmatched route clears the previous screen's feature.
    setFeature(routeToFeature('parent', routeId));
    setRoute(normalizeParentPath(routeId));
    capturePageview({ routeId, params: pickParams('parent', matches.at(-1)?.search) });
  } catch {
    // never load-bearing
  }
}

export function initRouterAnalytics(router: AnyRouter): void {
  try {
    router.subscribe('onResolved', () => emit(router));
  } catch {
    // never load-bearing
  }
  // Analytics init is async, so the first navigation may already have resolved; before that, matches are empty.
  if (router.state.status !== 'pending' && router.state.matches.length > 0) emit(router);
}
